// ────────────────────────────────────────────
// Enums
// ────────────────────────────────────────────

export enum CellStatus {
  QUEUED = "QUEUED",
  DISPATCHED = "DISPATCHED",
  RUNNING = "RUNNING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  STALE = "STALE",
}

export const PENDING_CELL_STATUSES = new Set<CellStatus>([
  CellStatus.QUEUED,
  CellStatus.DISPATCHED,
  CellStatus.RUNNING,
]);

export enum ColumnType {
  TEXT = "TEXT",
  PROMPT_TEMPLATE = "PROMPT_TEMPLATE",
  CODE_EXECUTION = "CODE_EXECUTION",
  COMPOSITION = "COMPOSITION",
  LLM_ASSERTION = "LLM_ASSERTION",
  WORKFLOW = "WORKFLOW",
  TRACE = "TRACE",
  REQUEST = "REQUEST",
  SMART_TABLE_REFERENCE = "SMART_TABLE_REFERENCE",
  ABSOLUTE_NUMERIC_DISTANCE = "ABSOLUTE_NUMERIC_DISTANCE",
  AI_DATA_EXTRACTION = "AI_DATA_EXTRACTION",
  APPLY_DIFF = "APPLY_DIFF",
  ASSERT_VALID = "ASSERT_VALID",
  COALESCE = "COALESCE",
  COMBINE_COLUMNS = "COMBINE_COLUMNS",
  COMPARE = "COMPARE",
  CONTAINS = "CONTAINS",
  CONVERSATION_SIMULATOR = "CONVERSATION_SIMULATOR",
  COSINE_SIMILARITY = "COSINE_SIMILARITY",
  COUNT = "COUNT",
  TRAJECTORY = "TRAJECTORY",
  DATASET = "DATASET",
  ENDPOINT = "ENDPOINT",
  FOR_LOOP = "FOR_LOOP",
  HUMAN = "HUMAN",
  JSON_PATH = "JSON_PATH",
  MATH_OPERATOR = "MATH_OPERATOR",
  MCP = "MCP",
  MIN_MAX = "MIN_MAX",
  PARSE_VALUE = "PARSE_VALUE",
  REGEX = "REGEX",
  REGEX_EXTRACTION = "REGEX_EXTRACTION",
  VARIABLE = "VARIABLE",
  WHILE_LOOP = "WHILE_LOOP",
  XML_PATH = "XML_PATH",
}

export type ReferenceType = "value" | "config";
export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | { [key: string]: JsonValue } | JsonValue[];

// ────────────────────────────────────────────
// Composition (cross-table column reference) DTOs
// ────────────────────────────────────────────

/**
 * A single column exposed by the composition-discovery endpoint. Scope is
 * always workspace-local; cross-workspace references are rejected by the
 * backend.
 */
export interface CompositionSourceColumn {
  id: string;
  title: string;
  type: ColumnType;
  config?: Record<string, unknown> | null;
}

/**
 * A sheet's worth of composable columns. Groups are scoped to a single
 * ``(smart_table, sheet)`` pair; a workspace with N tables × M sheets
 * returns up to N*M groups from ``GET .../columns/available-sources``.
 */
export interface CompositionSourceGroup {
  smart_table_id: string;
  smart_table_title: string;
  sheet_id: string;
  sheet_title: string;
  columns: CompositionSourceColumn[];
}

/**
 * Server-side query parameters for ``GET .../columns/available-sources``.
 *
 * The backend does NOT currently support a ``smart_table_id`` filter; scoping
 * to a single table is a client-side filter over the returned groups.
 *
 * ``output_only`` defaults to ``true`` server-side. The first-pass frontend
 * always sends ``false`` so every column is discoverable regardless of FR-15.
 */

// ────────────────────────────────────────────
// Core entities
// ────────────────────────────────────────────

export interface SmartTableSummary {
  id: string;
  title: string;
  folder_id?: number | null;
  sheet_count: number;
  created_at: string;
  updated_at: string;
}

export interface SmartTableDetail extends SmartTableSummary {
  owner?: string | null;
  sheet_row_counts?: Record<string, number>;
}

export type LegacySmartTableMigrationSourceType = "dataset_group" | "report";

export interface LegacySmartTableMigrationRequest {
  source_type: LegacySmartTableMigrationSourceType;
  source_id: number;
}

export interface LegacySmartTableMigrationResponse {
  success: boolean;
  smart_table?: Pick<SmartTableDetail, "id"> | null;
}

export interface SmartColumn {
  id: string;
  title: string;
  position_rank: number;
  type: ColumnType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  config: Record<string, any>;
  config_hash: string;
  is_output_column: boolean;
  dependencies: ColumnDependency[];
  is_virtual?: boolean;
  is_system_managed?: boolean;
  system_kind?: string | null;
  group_parent_column_id?: string | null;
  group_child_column_ids?: string[];
  metric_key?: string | null;
  metric_label?: string | null;
  /**
   * Candidate sibling column titles grouped by source type. Populated by
   * the backend so the column config sidebar can render the picker without
   * recomputing from `sheetDataStore.columns`.
   */
  available_sources?: Record<string, string[]>;
  /**
   * Sibling column titles currently referenced by this column's config,
   * derived from the dependency edges. Same shape as `available_sources`.
   */
  sources?: Record<string, string[]>;
}

export interface ColumnDependency {
  column_id: string;
  reference_type: ReferenceType;
  config_key?: string | null;
  config_meta?: Record<string, unknown> | null;
}

export interface TruncatedValueStub {
  __truncated: true;
  size: number;
  type: string;
  request_id?: string | number;
  trace_id?: string;
}

export const isTruncatedValueStub = (value: unknown): value is TruncatedValueStub => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as {
    __truncated?: unknown;
    size?: unknown;
    type?: unknown;
  };

  return (
    candidate.__truncated === true &&
    typeof candidate.size === "number" &&
    Number.isFinite(candidate.size) &&
    typeof candidate.type === "string"
  );
};

export interface SmartCell {
  id: string;
  row_index: number;
  smart_column_id: string;
  execution_id?: string | null;
  status: CellStatus;
  display_value: string | null;
  display_value_truncated?: boolean;
  metadata?: {
    display_type?: "boolean" | "number" | "object" | "string" | "unknown" | "html" | "markdown";
    [key: string]: unknown;
  } | null;
  value: JsonValue | TruncatedValueStub;
  input_hash: string | null;
  last_computed_version: number | null;
  updated_at: string;
  is_ref?: boolean;
  is_virtual?: boolean;
}

export interface GridRow {
  row_index: number;
  cells: Record<string, SmartCell>; // columnId → SmartCell
}

export type SmartCellStatusCounts = Record<CellStatus, number>;

export interface SheetStatusCountsResponse {
  success: boolean;
  status_counts: SmartCellStatusCounts;
  status_row_counts: SmartCellStatusCounts;
  total_cells: number;
  total_rows: number;
}

export interface GridResponse {
  columns?: SmartColumn[];
  rows: GridRow[];
  version: number;
  next_cursor: string | null;
  has_more: boolean;
  limit: number;
  row_count?: number;
}

export interface AddRowsResponse {
  rows_created: number;
  start_row_index: number;
  cell_count: number;
  row_count: number;
  version: number;
  rows: GridRow[];
}

export interface CreateColumnResponse {
  column: SmartColumn;
  columns?: SmartColumn[];
  cells: SmartCell[];
  version: number;
}

export interface SmartSheet {
  id: string;
  title: string;
  index: number;
  version_count: number;
  row_count?: number;
  /** Planned eval case count from Eval SDK (Redis-backed, may be absent). */
  expected_row_count?: number;
  /** Explicit Eval SDK populate lifecycle (Redis-backed, may be absent). */
  eval_run_status?: "running" | "completed" | "aborted";
  updated_at?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  score_config: Record<string, any> | null;
  columns: SmartColumn[];
  active_execution_ids?: string[];
  active_execution_count?: number;
  has_active_execution?: boolean;
  pending_cell_count?: number;
}

export interface SheetVersionCellTokenValue {
  value?: string | number | boolean | null;
  text?: string | number | boolean | null;
  [key: string]: unknown;
}

export interface SheetVersionCellSnapshot {
  status?: string;
  display_value?: string | null;
  value?: SheetVersionCellTokenValue | string | number | boolean | null | Record<string, unknown>;
  input_hash?: string | null;
  last_computed_version?: number | null;
}

export interface SheetVersionCellEditPayload {
  row_index?: number;
  column_id?: string;
  before?: SheetVersionCellSnapshot | null;
  after?: SheetVersionCellSnapshot | null;
}

export interface SheetVersionDelta {
  id?: string;
  change_type: string;
  payload: SheetVersionCellEditPayload | Record<string, unknown> | null;
  created_at?: string | null;
}

export interface SheetVersionMetadataActorUser {
  id: number;
  email?: string | null;
  name?: string | null;
}

export interface SheetVersionMetadataActor {
  id?: string | null;
  email?: string | null;
  name?: string | null;
  user?: SheetVersionMetadataActorUser | null;
}

export interface SheetVersionMetadataForkedFrom {
  id: string;
  version_number?: number | null;
}

export interface SheetVersionMetadataSourceVersion {
  id: string;
  version_number?: number | null;
}

export interface SheetVersionScoreMetadata {
  status?: "completed" | "missing";
  score_calculation_id?: string;
  aggregate_score?: number | null;
  scoring_type?: string | null;
  calculated_at?: string | null;
  scored_rows?: number;
  excluded_rows?: number;
  total_rows?: number;
  result?: Record<string, unknown>;
}

export interface SheetVersionLegacyMigrationMetadata {
  source_type?: string | null;
  source_id?: string | null;
}

export interface SheetVersionMetadata {
  created_by?: SheetVersionMetadataActor | null;
  forked_from_version?: SheetVersionMetadataForkedFrom | null;
  source_version?: SheetVersionMetadataSourceVersion | null;
  score?: SheetVersionScoreMetadata | null;
  legacy_migration?: SheetVersionLegacyMigrationMetadata | null;
}

export interface SheetVersionScoreHistoryPoint {
  version_id: string;
  version_number: number;
  version_name?: string | null;
  created_at: string | null;
  score: number;
  delta_count?: number | null;
  score_calculation_id?: string | null;
  scoring_type?: string | null;
  calculated_at?: string | null;
  scored_rows?: number | null;
  excluded_rows?: number | null;
  total_rows?: number | null;
}

export interface SheetVersionScoreHistory {
  points: SheetVersionScoreHistoryPoint[];
  total_points: number;
  returned_points: number;
  resolution: "raw" | "min_max_bucket";
  range?: "all" | "last_25" | "last_50" | "last_100" | "last_250";
  max_points?: number;
  is_sampled?: boolean;
}

export interface SheetVersion {
  id: string;
  sheet_id?: string;
  version_number: number;
  created_at: string;
  name?: string | null;
  created_by?: string;
  metadata?: SheetVersionMetadata | null;
  deltas?: SheetVersionDelta[];
  // Backward compatibility for older payload shape
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delta?: Record<string, any>;
  // Included on version detail endpoint
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  snapshot?: any;
}

export type SmartTableScoreMode =
  | "none"
  | "single_column"
  | "multi_column"
  | "custom"
  | "aggregate"
  | "scorecard";
export type SmartTableResolvedScoreType = "boolean" | "number";

export interface SmartTableScoreSummary {
  type?: SmartTableResolvedScoreType;
  value?: number | null;
  score?: number | null;
  num_skipped?: number;
  success_count?: number;
  total_count?: number;
}

export interface SmartTableScoreColumnDetail extends SmartTableScoreSummary {
  column_name: string;
  score_type?: SmartTableResolvedScoreType;
}

export interface SmartTableScoreColumnResult {
  column_id: string;
  title: string;
  score?: number | null;
  included_count?: number;
  excluded_count?: number;
  resolved_scoring_type?:
    | "auto"
    | "boolean"
    | "numeric"
    | "custom"
    | "aggregate"
    | "scorecard"
    | null;
  success_count?: number;
  total_count?: number;
}

export interface SmartTableAggregateTopValue {
  value: string;
  count: number;
  percentage?: number | null;
}

export interface SmartTableAggregateResult {
  aggregation_type?: "most_frequent_value" | "min_value" | "max_value";
  source_column?: { id: string; title: string };
  label_column?: { id: string; title: string } | null;
  winner?: string | null;
  value?: string | number | null;
  count?: number;
  total?: number;
  percentage?: number | null;
  row_index?: number | null;
  scored_rows?: number;
  excluded_rows?: number;
  top_values?: SmartTableAggregateTopValue[];
}

export interface SmartTableScoreDetails {
  message?: string;
  columns?: SmartTableScoreColumnDetail[];
  column_name?: string;
  score?: number | null;
  score_type?: SmartTableResolvedScoreType;
  num_skipped?: number;
  success_count?: number;
  total_count?: number;
  matrix?: SmartTableScoreMatrix;
  configuration?: Record<string, unknown>;
  aggregate_result?: SmartTableAggregateResult;
}

export interface SmartTableScoreMatrixCellObject {
  value: string | number;
  positive_metric?: boolean | null;
}

export type SmartTableScoreMatrixCell = string | number | SmartTableScoreMatrixCellObject;
export type SmartTableScoreMatrix = SmartTableScoreMatrixCell[][][];

export type SmartTableScoreStatus = "queued" | "running" | "completed" | "failed";

export interface SmartTableScoreConfigurationMetadata {
  score_type: "auto" | "boolean" | "numeric" | "custom" | "aggregate";
  score_config: Record<string, unknown>;
}

export interface ScoreResult {
  success?: boolean;

  // Parity shape (aligned with eval score consumers)
  score_type?: SmartTableScoreMode;
  overall_score?: number | null;
  details?: SmartTableScoreDetails;

  // Smart-table scoring lifecycle metadata
  status?: SmartTableScoreStatus | null;
  has_custom_scoring?: boolean;
  score_calculation_error?: string | null;
  score_configuration?: SmartTableScoreConfigurationMetadata | null;
  score_matrix?: SmartTableScoreMatrix;
  aggregate_score?: number | null;
  total_rows?: number;
  scored_rows?: number;
  excluded_rows?: number;

  // Existing/legacy shape used by current Table UI
  columns?: SmartTableScoreColumnResult[];
  aggregate?: Record<string, unknown>;
  per_column?: Record<string, unknown>;
}

// ────────────────────────────────────────────
// Request payloads
// ────────────────────────────────────────────

export interface CreateSmartTableRequest {
  title?: string;
  folder_id?: number | null;
}

export interface PromptTemplateEngineOverride {
  provider?: string | null;
  model: string;
  parameters?: Record<string, unknown>;
  model_config_id?: number | null;
  provider_id?: number | null;
  provider_base_url_name?: string | null;
  api_type?: string | null;
}

export interface CreateModelComparisonPresetRequest {
  prompt_registry_id: number;
  prompt_version_number: number;
  title?: string;
  folder_id?: number | null;
  initial_inputs?: Record<string, string>;
  models: {
    label: string;
    engine: PromptTemplateEngineOverride;
  }[];
}

export interface CreateModelComparisonPresetResponse {
  success: boolean;
  smart_table: SmartTableDetail;
  sheet?: SmartSheet;
}

export type EvaluationPresetType = "GROUND_TRUTH_COMPARE" | "STRUCTURAL";

export interface CreateEvaluationPresetRequest {
  prompt_registry_id: number;
  prompt_version_number: number;
  preset_type: EvaluationPresetType;
  title?: string;
  folder_id?: number | null;
  initial_inputs?: Record<string, string>;
  expected_value?: string;
  assertions: string[];
}

export interface CreateEvaluationPresetResponse {
  success: boolean;
  smart_table: SmartTableDetail;
  sheet?: SmartSheet;
}

export interface CreateCustomPresetRequest {
  prompt_registry_id: number;
  prompt_version_number: number;
  title?: string;
  folder_id?: number | null;
  initial_inputs?: Record<string, string>;
}

export interface CreateCustomPresetResponse {
  success: boolean;
  smart_table: SmartTableDetail;
  sheet?: SmartSheet;
}

export interface CreateBrandVisibilityPresetRequest {
  title?: string;
  folder_id?: number | null;
  domain: string;
  queries: string[];
  models: {
    label: string;
    engine: PromptTemplateEngineOverride;
  }[];
}

export interface CreateBrandVisibilityPresetResponse {
  success: boolean;
  smart_table: SmartTableDetail;
  sheet?: SmartSheet;
}

export interface UpdateSmartTableRequest {
  title?: string;
  folder_id?: number | null;
}

export interface CreateSheetRequest {
  title: string;
  index?: number;
}

export interface UpdateSheetRequest {
  title?: string;
  index?: number;
}

export interface CreateColumnRequest {
  title: string;
  type: ColumnType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  config?: Record<string, any>;
  dependencies?: ColumnDependency[];
  is_output_column?: boolean;
  left_neighbor_column_id?: string | null;
  version_batch_id?: string;
  version_batch_kind?: "column_setup";
}

export interface UpdateColumnRequest {
  title?: string;
  type?: ColumnType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  config?: Record<string, any>;
  dependencies?: ColumnDependency[];
  is_output_column?: boolean;
  version_batch_id?: string;
  version_batch_kind?: "column_setup";
}

export interface MoveColumnRequest {
  left_neighbor_column_id?: string | null;
  right_neighbor_column_id?: string | null;
}

export interface DuplicateColumnRequest {
  title?: string;
}

export interface SetDependenciesRequest {
  dependencies: ColumnDependency[];
}

export interface UpdateCellRequest {
  display_value?: string | null;
  value?: JsonValue;
}

export interface AddRowsRequest {
  count: number;
  values?: Record<string, JsonValue>[];
}

export interface DeleteRowsRequest {
  row_indices: number[];
}

export interface CopyRowsRequest {
  row_indices: number[];
  target_sheet_id: string;
}

export interface RecalculateColumnRequest {
  row_start?: number;
  row_end?: number;
  force_recompute?: boolean;
  confirmation_token?: string;
}

export interface BatchRecalculateRequest {
  column_ids?: string[];
  row_ids?: number[];
  /**
   * Cell statuses to execute. When omitted, the backend defaults to
   * [STALE] so COMPLETED cells are not silently re-run. Pass an
   * empty array to run every cell regardless of status.
   */
  statuses?: CellStatus[];
  confirmation_token?: string;
}

export interface ConfirmComputeRequest {
  confirmation_token: string;
  column_id?: string;
  row_range?: [number, number];
}

export type SmartTableConfigureScoreType = "auto" | "boolean" | "numeric" | "custom" | "aggregate";

export interface SmartTableBooleanScoreConfig {
  column_ids: string[];
  true_values?: string[];
  false_values?: string[];
  assertion_aggregation?: "all" | "any" | "mean";
}

export interface SmartTableNumericOrAutoScoreConfig {
  column_ids: string[];
}

export interface SmartTableCustomScoreConfig {
  code: string;
  code_language?: "PYTHON" | "JAVASCRIPT";
}

export interface SmartTableAggregateScoreConfig {
  aggregation_type: "most_frequent_value" | "min_value" | "max_value";
  source_column_id: string;
  label_column_id?: string | null;
}

export type SmartTableConfigureScoreConfig =
  | SmartTableBooleanScoreConfig
  | SmartTableNumericOrAutoScoreConfig
  | SmartTableCustomScoreConfig
  | SmartTableAggregateScoreConfig;

export interface ConfigureScoreRequest {
  // Current backend contract
  score_type?: SmartTableConfigureScoreType;
  score_config?: SmartTableConfigureScoreConfig;

  // Backward-compatible client shape (mapped server-side or by caller)
  column_ids?: string[];
  scoring_type?: "boolean" | "numeric";
  config?: Record<string, unknown>;
}

export interface CreateVersionRequest {
  name?: string;
  source_version_id?: string;
}

// ────────────────────────────────────────────
// List/pagination wrappers
// ────────────────────────────────────────────

export interface CursorPaginatedResponse<T> {
  items: T[];
  next_cursor: string | null;
  has_more: boolean;
  limit: number;
}

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const SMART_TABLE_META_KEYS = new Set<string>([
  "success",
  "message",
  "error",
  "limit",
  "next_cursor",
  "has_more",
  "version",
  "stale_count",
  "total_cells",
  "execution_id",
  "rows_created",
  "start_row_index",
  "cell_count",
  "selected_cell_count",
  "cells_deleted",
  "row_indices",
  "cancelled_count",
  "execution_ids",
  "requires_confirmation",
  "confirmation_token",
  "affected_row_count",
  "affected_cell_count",
]);

/**
 * Unwraps SmartTable API responses that are commonly wrapped as:
 * - { success: true, smart_table: {...} }
 * - { success: true, sheets: [...] }
 * - { success: true, cell: {...}, version: 2 }
 *
 * If no preferred key is found, it falls back to:
 * - The first non-meta key (if exactly one exists), otherwise
 * - The payload itself (already-unwrapped shape).
 */
export const unwrapSmartTablePayload = <T>(
  payload: unknown,
  preferredKeys: readonly string[],
): T => {
  if (!isRecord(payload)) {
    return payload as T;
  }

  for (const key of preferredKeys) {
    if (key in payload) {
      return payload[key] as T;
    }
  }

  const candidateKeys = Object.keys(payload).filter(key => !SMART_TABLE_META_KEYS.has(key));

  if (candidateKeys.length === 1) {
    return payload[candidateKeys[0]] as T;
  }

  return payload as T;
};

export const unwrapSmartTableEntityPayload = <T>(payload: unknown, key: string): T =>
  unwrapSmartTablePayload<T>(payload, [key]);

export const unwrapSmartTableCollectionPayload = <T>(payload: unknown, key: string): T[] =>
  unwrapSmartTablePayload<T[]>(payload, [key]);

export const unwrapSmartTableCursorCollectionPayload = <T>(
  payload: unknown,
  key: string,
): CursorPaginatedResponse<T> => {
  const items = unwrapSmartTableCollectionPayload<T>(payload, key);

  return {
    items,
    next_cursor:
      isRecord(payload) && typeof payload.next_cursor === "string" ? payload.next_cursor : null,
    has_more: isRecord(payload) && payload.has_more === true,
    limit: isRecord(payload) && typeof payload.limit === "number" ? payload.limit : items.length,
  };
};

export const unwrapCreateColumnResponse = (payload: unknown): CreateColumnResponse => {
  const column = unwrapSmartTableEntityPayload<SmartColumn>(payload, "column");
  const columns =
    isRecord(payload) && Array.isArray(payload.columns)
      ? (payload.columns as SmartColumn[])
      : undefined;
  const cells =
    isRecord(payload) && Array.isArray(payload.cells) ? (payload.cells as SmartCell[]) : [];
  const version = isRecord(payload) && typeof payload.version === "number" ? payload.version : 0;

  return {
    column,
    columns,
    cells,
    version,
  };
};
